/**
 * The theme control: one attached group of two buttons. Never a `<select>`.
 *
 * - The first button shows the theme on screen (sun or moon, no text). Pressing it picks the opposite theme as an explicit choice. It is
 *   pressed (`aria-pressed`) when the reader made an explicit choice.
 * - The second button (a monitor), is pressed while the page follows the
 *   operating system. Pressing it returns to that.
 *
 * The default is System. An explicit choice lives in `localStorage` under one
 * key; System removes the key. Storage can fail (a private window, blocked
 * site data), so every access is guarded and the page still draws. While
 * System is active, a change of the operating system theme applies at once.
 * The rules live in `themeMode.ts`.
 */
import {
  preferenceAfterTogglePress,
  parseStoredPreference,
  resolveTheme,
  type Theme,
  type ThemePreference,
} from './themeMode'

const STORAGE_KEY = 'gibs-landing-theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/** The attribute that tells the two buttons apart. */
export const THEME_ACTION_ATTRIBUTE = 'data-theme-action'

const readStoredPreference = (): ThemePreference => {
  try {
    return parseStoredPreference(localStorage.getItem(STORAGE_KEY))
  } catch {
    // Storage is blocked: follow the system on every visit.
    return 'system'
  }
}

const writeStoredPreference = (preference: ThemePreference): void => {
  try {
    if (preference === 'system') {
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Storage may be unavailable. The choice still applies for this visit.
  }
}

const readSystemTheme = (): Theme => (window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light')

const SUN_SHAPES: readonly (readonly [string, Record<string, string>])[] = [
  ['circle', { cx: '12', cy: '12', r: '4' }],
  ['path', { d: 'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41' }],
]

const MOON_SHAPES: readonly (readonly [string, Record<string, string>])[] = [
  ['path', { d: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z' }],
]

const MONITOR_SHAPES: readonly (readonly [string, Record<string, string>])[] = [
  ['rect', { width: '20', height: '14', x: '2', y: '3', rx: '2' }],
  ['path', { d: 'M8 21h8M12 17v4' }],
]

/** The icons of the group. */
export type ThemeIconName = Theme | 'system'

const SHAPES_BY_ICON: Record<ThemeIconName, typeof SUN_SHAPES> = {
  light: SUN_SHAPES,
  dark: MOON_SHAPES,
  system: MONITOR_SHAPES,
}

/** The words for the first button: the press picks the opposite theme. */
export const toggleLabel = (theme: Theme): string => `Switch to ${theme === 'dark' ? 'light' : 'dark'}`

/** The words for the second button. */
export const SYSTEM_LABEL = 'Follow the system theme'

/** The lucide sun, moon or monitor, in the text colour. Decorative. */
export const createThemeIcon = (theme: ThemeIconName): SVGSVGElement => {
  const icon = document.createElementNS(SVG_NAMESPACE, 'svg')
  icon.setAttribute('class', 'theme-icon')
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('width', '1em')
  icon.setAttribute('height', '1em')
  icon.setAttribute('fill', 'none')
  icon.setAttribute('stroke', 'currentColor')
  icon.setAttribute('stroke-width', '2')
  icon.setAttribute('stroke-linecap', 'round')
  icon.setAttribute('stroke-linejoin', 'round')
  icon.setAttribute('aria-hidden', 'true')
  for (const [tagName, attributes] of SHAPES_BY_ICON[theme]) {
    const shape = document.createElementNS(SVG_NAMESPACE, tagName)
    for (const [name, value] of Object.entries(attributes)) {
      shape.setAttribute(name, value)
    }
    icon.append(shape)
  }
  return icon
}

/** Draws the group's state: what each button shows and whether it is pressed. */
const drawControl = (
  toggleButton: HTMLButtonElement,
  systemButton: HTMLButtonElement,
  state: { preference: ThemePreference; theme: Theme },
): void => {
  document.documentElement.dataset.theme = state.theme
  toggleButton.replaceChildren(createThemeIcon(state.theme))
  toggleButton.setAttribute('aria-pressed', String(state.preference !== 'system'))
  toggleButton.setAttribute('aria-label', toggleLabel(state.theme))
  toggleButton.title = toggleLabel(state.theme)
  systemButton.setAttribute('aria-pressed', String(state.preference === 'system'))
  systemButton.setAttribute('aria-label', SYSTEM_LABEL)
  systemButton.title = SYSTEM_LABEL
  if (systemButton.childElementCount === 0) systemButton.append(createThemeIcon('system'))
}

/**
 * Wires the two buttons inside `group` (found by {@link THEME_ACTION_ATTRIBUTE}
 * values `toggle` and `system`). Does nothing when either is missing.
 */
export const initializeThemeControl = (group: ParentNode): void => {
  const toggleButton = group.querySelector(`[${THEME_ACTION_ATTRIBUTE}="toggle"]`)
  const systemButton = group.querySelector(`[${THEME_ACTION_ATTRIBUTE}="system"]`)
  if (!(toggleButton instanceof HTMLButtonElement) || !(systemButton instanceof HTMLButtonElement)) {
    return
  }

  let preference = readStoredPreference()
  const systemQuery = window.matchMedia(DARK_QUERY)

  const draw = (): void =>
    drawControl(toggleButton, systemButton, {
      preference,
      theme: resolveTheme(preference, readSystemTheme()),
    })

  const choose = (next: ThemePreference): void => {
    preference = next
    writeStoredPreference(next)
    draw()
  }

  toggleButton.addEventListener('click', () => {
    choose(preferenceAfterTogglePress(preference, readSystemTheme()))
  })
  systemButton.addEventListener('click', () => choose('system'))
  systemQuery.addEventListener('change', () => {
    if (preference === 'system') {
      draw()
    }
  })

  draw()
}
